import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import {
  PersonalDetails,
  SocialMedia,
  User,
  AuthorizationParams,
  PublicUserRole,
  SearchParams,
  SortParam,
  getURLParams
} from '@mprolab/sdk'
import getApiHeaders from 'utils/getApiHeaders'
import { getUsersServiceBaseQueryUrl, warnIfUsersServiceMissing } from 'utils/serviceBaseUrls'

warnIfUsersServiceMissing()

export enum SortUserKey {
  CREATED_AT = 'createdAt',
  VISITED_AT = 'visitedAt',
  ENGAGEMENT_POINTS = 'engagementPoints'
}

export interface GetUserProfilesParams {
  skip?: number
  limit?: number
  wallets?: string[]
  roles?: PublicUserRole[]
  elympicsUserIds?: string[]
  sort?: SortParam<SortUserKey>
}
export interface UpdateUserParams extends AuthorizationParams {
  socialMedia: SocialMedia[]
  personalDetails: PersonalDetails
}

export interface GetUsernameAvailabilityParams extends AuthorizationParams {
  userName: string
}

export interface AddNewWalletParams extends AuthorizationParams {
  wallet: string
  chainId: number
  signature: string
  merge?: boolean
}

export interface DisconnectExternalParams extends AuthorizationParams {
  externalName: string
}

enum UserApiTag {
  USER = 'User'
}

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: fetchBaseQuery({ baseUrl: getUsersServiceBaseQueryUrl() }),
  tagTypes: [UserApiTag.USER],
  endpoints: (builder) => ({
    me: builder.query<User, AuthorizationParams>({
      query: (params) => {
        const { account, userId, accessToken } = params
        return {
          url: 'me',
          method: 'GET',
          headers: getApiHeaders({ account, userId, accessToken })
        }
      },
      transformResponse(rawResult) {
        return rawResult as User
      },
      providesTags: [{ type: UserApiTag.USER, id: 'User' }]
    }),
    updateUser: builder.mutation<User, UpdateUserParams>({
      query: (params) => {
        const { account, userId, accessToken, socialMedia, personalDetails } = params
        return {
          url: 'update',
          method: 'PATCH',
          body: { socialMedia, personalDetails },
          headers: getApiHeaders({ account, userId, accessToken })
        }
      },
      invalidatesTags: [{ type: UserApiTag.USER, id: 'User' }]
    }),
    addNewWallet: builder.mutation<User, AddNewWalletParams>({
      query: (params) => {
        const { account, userId, accessToken, wallet, chainId, merge, signature } = params
        return {
          url: 'auth/new-address',
          method: 'POST',
          body: { wallet, chainId, merge, signature },
          headers: getApiHeaders({ account, userId, accessToken })
        }
      },
      invalidatesTags: [{ type: UserApiTag.USER, id: 'User' }]
    }),
    checkUserNameAvailability: builder.query<
      {
        isAvailable: boolean
      },
      GetUsernameAvailabilityParams
    >({
      query: (params) => {
        const { account, userId, accessToken, userName } = params
        return {
          url: `availability/${encodeURIComponent(userName)}`,
          method: 'GET',
          headers: getApiHeaders({ account, userId, accessToken })
        }
      }
    }),
    getPublicProfile: builder.query<User, string>({
      query: (userNameOrAddress) => {
        return {
          url: `profile/${encodeURIComponent(userNameOrAddress)}`,
          method: 'GET'
        }
      }
    }),
    getUserProfiles: builder.query<{ count: number; results: User[] }, GetUserProfilesParams>({
      query: (params) => {
        const requestParams = getURLParams({ params })

        return {
          url: 'profiles',
          method: 'GET',
          params: requestParams
        }
      }
    }),
    getUsersSearch: builder.query<{ results: User[]; count: number }, SearchParams>({
      query: (params) => {
        const { searchText } = params
        const requestParams = getURLParams({ params, paramsToSkip: ['searchText'] })

        return {
          url: `users-search/${encodeURIComponent(searchText)}`,
          method: 'GET',
          params: requestParams
        }
      }
    }),
    disconnectExternal: builder.mutation<User, DisconnectExternalParams>({
      query: (params) => {
        const { account, userId, accessToken, externalName } = params

        return {
          url: 'disconnect-external-account',
          method: 'PATCH',
          body: { externalName },
          headers: getApiHeaders({ account, userId, accessToken })
        }
      },
      invalidatesTags: [{ type: UserApiTag.USER, id: 'User' }]
    }),
    removeWallet: builder.mutation<User, AuthorizationParams>({
      query: (params) => {
        const { account, userId, accessToken } = params

        return {
          url: `auth/remove/${account}`,
          method: 'DELETE',
          headers: getApiHeaders({ account, userId, accessToken })
        }
      },
      invalidatesTags: [{ type: UserApiTag.USER, id: 'User' }]
    })
  })
})

export const {
  useMeQuery,
  useLazyMeQuery,
  useUpdateUserMutation,
  useAddNewWalletMutation,
  useCheckUserNameAvailabilityQuery,
  useLazyCheckUserNameAvailabilityQuery,
  useGetPublicProfileQuery,
  useLazyGetPublicProfileQuery,
  useGetUserProfilesQuery,
  useLazyGetUserProfilesQuery,
  useGetUsersSearchQuery,
  useLazyGetUsersSearchQuery,
  useDisconnectExternalMutation,
  useRemoveWalletMutation
} = userApi
